import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getClientPriceLists,
  getClientPriceListDetails,
  updateClientPriceList,
  deleteClientPriceList,
  ClientPriceList,
  ClientPriceListItem,
} from '../lib/clientPriceListApi';
import { ArrowRight, CheckCircle, Pencil, Trash2 } from 'lucide-react';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import { Modal } from '../components/Modal';
import ClientPriceListEmailDialog from '../components/ClientPriceListEmailDialog';
import StatusBadge from '../components/StatusBadge';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import SearchInput from '../components/ui/SearchInput';
import { EmptyState } from '../components/ui/States';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { useToast } from '../context/ToastContext';
import { formatDate, formatDateTime } from '../utils/dateFormat';

function formatSource(source?: string) {
  if (!source) return '—';
  if (source === 'quotation_historical') return 'Historical Quotation';
  if (source === 'vendor_invoice') return 'Vendor Invoice';
  return source;
}

export default function ClientPriceListsPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const openIdFromUrl = searchParams.get('openId');

  const [lists, setLists] = useState<ClientPriceList[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [itemsModalList, setItemsModalList] = useState<ClientPriceList | null>(null);
  const [modalMarkups, setModalMarkups] = useState<Record<number, number>>({});
  const [modalQuantities, setModalQuantities] = useState<Record<number, number>>({});
  const [modalRemarks, setModalRemarks] = useState<Record<number, string>>({});
  const [modalInternalRemarks, setModalInternalRemarks] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendList, setSendList] = useState<ClientPriceList | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  const fetchLists = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getClientPriceLists({ page, limit });
      setLists(res.data || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error('Failed to load client price lists:', e);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  useEffect(() => {
    if (!openIdFromUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getClientPriceListDetails(openIdFromUrl);
        if (!cancelled && res?.data) {
          setItemsModalList(res.data);
          const initialMarkups: Record<number, number> = {};
          const initialQty: Record<number, number> = {};
          const initialRemarks: Record<number, string> = {};
          const initialInternalRemarks: Record<number, string> = {};
          (res.data.items || []).forEach((item, idx) => {
            initialMarkups[idx] = item.markup ?? 5;
            initialQty[idx] = typeof item.quantity === 'number' && item.quantity >= 0 ? item.quantity : 0;
            initialRemarks[idx] = item.remarks ?? '';
            initialInternalRemarks[idx] = item.internal_remarks ?? '';
          });
          setModalMarkups(initialMarkups);
          setModalQuantities(initialQty);
          setModalRemarks(initialRemarks);
          setModalInternalRemarks(initialInternalRemarks);
          setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            p.delete('openId');
            return p;
          });
        }
      } catch (e) {
        console.error('Failed to load client price list:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openIdFromUrl, setSearchParams]);

  const filteredLists = useMemo(() => {
    if (!search.trim()) return lists;
    const q = search.trim().toLowerCase();
    return lists.filter(
      (l) =>
        l.name?.toLowerCase().includes(q) ||
        String(l.items?.length ?? 0).includes(q)
    );
  }, [lists, search]);

  const openItemsModal = useCallback((list: ClientPriceList) => {
    setItemsModalList(list);
    const initialMarkups: Record<number, number> = {};
    const initialQty: Record<number, number> = {};
    const initialRemarks: Record<number, string> = {};
    const initialInternalRemarks: Record<number, string> = {};
    (list.items || []).forEach((item, idx) => {
      initialMarkups[idx] = item.markup ?? 5;
      initialQty[idx] = typeof item.quantity === 'number' && item.quantity >= 0 ? item.quantity : 0;
      initialRemarks[idx] = item.remarks ?? '';
      initialInternalRemarks[idx] = item.internal_remarks ?? '';
    });
    setModalMarkups(initialMarkups);
    setModalQuantities(initialQty);
    setModalRemarks(initialRemarks);
    setModalInternalRemarks(initialInternalRemarks);
  }, []);

  const closeItemsModal = useCallback(() => {
    setItemsModalList(null);
    setModalMarkups({});
    setModalQuantities({});
    setModalRemarks({});
    setModalInternalRemarks({});
    setSendModalOpen(false);
    setSaveSuccess(false);
  }, []);

  // The client's link needs the list's secret (D116.1), which a list ROW never
  // carries: read the list from the staff details route, which makes one if the
  // list predates it, and hand THAT to the send dialog.
  const openSendModal = useCallback(async () => {
    if (!itemsModalList) return;
    try {
      const res = await getClientPriceListDetails(itemsModalList._id);
      setSendList(res.data);
      setSendModalOpen(true);
    } catch (e) {
      console.error('Failed to open the send dialog:', e);
      toast.error('Could not open the send window. Nothing was sent.');
    }
  }, [itemsModalList, toast]);

  const handleSaveMarkups = async () => {
    if (!itemsModalList?.items?.length) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      const itemsPayload = itemsModalList.items.map((_, idx) => ({
        markup: modalMarkups[idx] ?? 5,
        quantity: modalQuantities[idx] ?? 0,
        remarks: modalRemarks[idx] ?? '',
        internal_remarks: modalInternalRemarks[idx] ?? '',
      }));
      await updateClientPriceList(itemsModalList._id, { items: itemsPayload });
      const res = await getClientPriceListDetails(itemsModalList._id);
      if (res?.data) {
        setItemsModalList(res.data);
        setLists((prev) => prev.map((l) => (l._id === res.data._id ? res.data : l)));
      }
      setSaveSuccess(true);
    } catch (e) {
      console.error('Failed to save markups:', e);
      toast.error('Could not save those markups. Nothing was changed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (list: ClientPriceList) => {
    const ok = await confirm({
      title: `Delete price list "${list.name}"?`,
      summary: 'The list and every item on it are removed. This cannot be undone.',
      tone: 'error',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await deleteClientPriceList(list._id);
      if (itemsModalList?._id === list._id) closeItemsModal();
      fetchLists();
    } catch (e) {
      console.error('Failed to delete:', e);
      toast.error('Could not delete that price list. Nothing was removed.');
    }
  };

  const itemPriceAndVat = (item: ClientPriceListItem, idx: number) => {
    let itemPriceTT: number;
    if (item.base_price != null) {
      const markupPct = modalMarkups[idx] ?? item.markup ?? 5;
      itemPriceTT = item.base_price * (1 + markupPct / 100);
    } else {
      itemPriceTT = typeof item.unit_price === 'number' ? item.unit_price : 0;
    }
    const qty = modalQuantities[idx] ?? (typeof item.quantity === 'number' && item.quantity >= 0 ? item.quantity : 0);
    const extensionPrice = itemPriceTT * qty;
    // base_price/unit_price already have VAT baked in when the item was added to the
    // price list (see masterCatalogue.controller's createPriceListFromCatalogue), so the
    // extension total is the final price — no second VAT multiplication.
    const priceWithVat = extensionPrice;
    return { itemPriceTT, extensionPrice, priceWithVat };
  };

  const totalItemsCount = lists.reduce((sum, l) => sum + (l.items?.length ?? 0), 0);

  const CLIENT_PRICE_LIST_INFO_CONTENT = (
    <div className="flex flex-col gap-3">
      <p>The Price List contains items provided by the client.</p>
      <p>It includes item details such as price in TT$ and USD, category and sub category, vendor name, vendor email ID, and the date created.</p>
      <p className="font-medium">From the Price List, you can:</p>
      <ul className="list-disc list-inside space-y-1 pl-1">
        <li>Export the price list</li>
        <li>Select items and share them with a client or vendor</li>
        <li>Upload a new price list</li>
      </ul>
      <p className="font-medium mt-2">Updated Requirements (30/1/26):</p>
      <ul className="list-disc list-inside space-y-1 pl-1">
        <li><strong>Master Catalog:</strong> Items with Cost Price</li>
        <li><strong>Price List:</strong> with Selling Price w/ Markup</li>
        <li>For each client &amp; Vessel different list</li>
        <li>Save all List; Add item quantity; Remarks per item</li>
        <li>Choose items; REM SALTIRE as references</li>
        <li>Unit price is only shown in system; Column to add quantity</li>
        <li>Share system link to client, to add quantity &amp; client can edit &amp; see total calculation</li>
        <li>Price in USD; Column for Pack &amp; Unit</li>
      </ul>
    </div>
  );

  return (
    /* `min-h-screen` removed — this page renders inside the app shell, which is
       already full height, so the extra minimum only added a second scrollbar. */
    <div className="bg-brand-bg">
      {/* The app's header band, and the sidebar's own label for this screen. */}
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Client Price List</h1>
            <ModuleInfoButton content={CLIENT_PRICE_LIST_INFO_CONTENT} ariaLabel="Client Price List information" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput value={search} onChange={setSearch} placeholder="Search here" className="w-64" />
            {/* EXPORT has never been wired to anything — it is disabled and says
                so rather than looking live and doing nothing when clicked. */}
            <Button disabled title="Export is not available yet">
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="w-full min-w-0 overflow-x-auto px-4 py-4 sm:px-8">
        {loading ? (
          <div className="py-12 text-center">
            <LoadingState text="Loading price lists..." />
          </div>
        ) : filteredLists.length === 0 ? (
          <EmptyState
            title="No client price lists yet"
            detail="Create one from the Master Catalog: select items there and choose “+ Price list”."
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-[338px]">
                    List name {lists.length > 0 && <span className="font-normal text-slate-400">{lists.length}</span>}
                  </th>
                  <th className="w-[200px]">Created</th>
                  <th className="w-[142px] text-right">
                    Total items {totalItemsCount > 0 && <span className="font-normal text-slate-400">{totalItemsCount}</span>}
                  </th>
                  <th className="w-[174px]">Status</th>
                  <th className="w-[128px]">Actions</th>
                  <th className="w-[98px]">
                    <span className="sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredLists.map((list) => {
                  const itemCount = list.items?.length ?? 0;
                  const isSent = !!list.sentAt;
                  return (
                    <tr key={list._id}>
                      <td className="font-medium text-slate-900">{list.name}</td>
                      <td className="whitespace-nowrap text-slate-500">{formatDate(list.createdAt, '—')}</td>
                      <td className="text-right tabular-nums">{itemCount}</td>
                      <td>
                        <StatusBadge status={isSent ? 'Sent' : 'Not sent'} />
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openItemsModal(list)}
                            title="Edit"
                            aria-label="Edit"
                            icon={<Pencil aria-hidden="true" className="h-4 w-4" />}
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(list)}
                            title="Delete"
                            aria-label="Delete"
                            icon={<Trash2 aria-hidden="true" className="h-4 w-4" />}
                          />
                        </div>
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/client-price-lists/${list._id}`)}
                          title="View details"
                          aria-label="View details"
                          icon={<ArrowRight aria-hidden="true" className="h-4 w-4" />}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination page={page} pageSize={limit} total={total} onPageChange={setPage} noun="price lists" />
          </div>
        )}
      </div>

      {/* Items popup – editable markup */}
      {itemsModalList && (
        /* Was a hand-built overlay with its own scrim, its own z-index and a 2px
           backdrop blur. It is the app's `Modal` now: one portal, one Escape
           handler, one z-layer, and no blur (the ActionDialog incident). */
        <Modal
          isOpen
          onClose={closeItemsModal}
          title={itemsModalList.name}
          fullHeight
          maxWidthClass="max-w-[90vw]"
          bodyClass="flex-1 min-h-0 min-w-0 max-w-full overflow-auto"
        >
          <div>
            <div>
              <table className="data-table data-table-grid data-table-dense min-w-[1400px]">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th>Item name</th>
                    <th>Last updated</th>
                    <th>Category</th>
                    <th>Sub category</th>
                    <th>Source</th>
                    <th>Unit</th>
                    <th className="text-right">Quantity</th>
                    <th className="text-right">Item price (US$)</th>
                    <th className="text-right">Markup</th>
                    <th className="text-right">Extension price</th>
                    <th className="text-right">VAT to be added</th>
                    <th className="text-right">Price with VAT</th>
                    <th>Remarks</th>
                    <th>Internal remarks</th>
                    <th>Keywords</th>
                  </tr>
                </thead>
                <tbody>
                  {(itemsModalList.items || []).map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.description || '—'}</td>
                      <td className="whitespace-nowrap">{formatDateTime(item.last_updated, '—')}</td>
                      <td>{item.category || '—'}</td>
                      <td>{item.sub_category || '—'}</td>
                      <td>{formatSource(item.source)}</td>
                      <td>{item.packaging || '—'}</td>
                      <td className="text-right">
                        <input
                          type="number"
                          min={0}
                          aria-label="Quantity"
                          value={modalQuantities[idx] ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? 0 : parseFloat(e.target.value);
                            setModalQuantities((prev) => ({ ...prev, [idx]: Number.isFinite(v) && v >= 0 ? v : 0 }));
                          }}
                          className="w-16 rounded border border-slate-200 px-2 py-1 text-right text-xs tabular-nums"
                        />
                      </td>
                      <td className="text-right tabular-nums">
                        ${itemPriceAndVat(item, idx).itemPriceTT.toFixed(2)}
                      </td>
                      <td className="text-right">
                        <input
                          type="number"
                          step={0.01}
                          min={-100}
                          aria-label="Markup"
                          value={modalMarkups[idx] ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? 0 : parseFloat(e.target.value);
                            setModalMarkups((prev) => ({ ...prev, [idx]: Number.isFinite(v) ? v : prev[idx] ?? 5 }));
                          }}
                          className="w-20 rounded border border-slate-200 px-2 py-1 text-right text-xs tabular-nums"
                        />
                      </td>
                      <td className="text-right tabular-nums">
                        ${itemPriceAndVat(item, idx).extensionPrice.toFixed(2)}
                      </td>
                      <td className="text-right">{item.vat ? 'Yes' : 'No'}</td>
                      <td className="text-right tabular-nums">
                        ${itemPriceAndVat(item, idx).priceWithVat.toFixed(2)}
                      </td>
                      <td>
                        <input
                          type="text"
                          aria-label="Remarks"
                          value={modalRemarks[idx] ?? ''}
                          onChange={(e) => setModalRemarks((prev) => ({ ...prev, [idx]: e.target.value }))}
                          placeholder="—"
                          className="w-full min-w-[80px] rounded border border-slate-200 px-2 py-1 text-xs"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          aria-label="Internal remarks"
                          value={modalInternalRemarks[idx] ?? ''}
                          onChange={(e) => setModalInternalRemarks((prev) => ({ ...prev, [idx]: e.target.value }))}
                          placeholder="—"
                          className="w-full min-w-[80px] rounded border border-slate-200 px-2 py-1 text-xs"
                        />
                      </td>
                      <td>{item.keywords?.length ? item.keywords.join(', ') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-slate-200 bg-white px-4 py-3">
              {saveSuccess && (
                <span className="flex items-center gap-1 text-sm font-medium text-emerald-700">
                  <CheckCircle aria-hidden="true" className="h-4 w-4" />
                  Saved
                </span>
              )}
              <Button onClick={handleSaveMarkups} loading={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="primary" onClick={openSendModal}>
                Send
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <ClientPriceListEmailDialog
        open={sendModalOpen}
        list={sendList}
        onClose={() => setSendModalOpen(false)}
        onSent={async () => {
          toast.success('Price list sent.');
          closeItemsModal();
          await fetchLists();
        }}
        /* Above the items Modal it opens from. That Modal is z-70 (the app's one
           modal layer), so the old z-60 would now put this dialog behind it. */
        zIndexClassName="z-[75]"
      />
    </div>
  );
}
